import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Tag, Palette, TrendingUp, DollarSign, BarChart3, PieChart, Filter } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useTagStore from '../../store/tagStore';
import useExpenseStore from '../../store/expenseStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Modal } from '../../components/ui/modal';
import { formatCurrency, formatDate } from '../../utils/formatters';

const TAG_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E',
  '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
  '#8B5CF6', '#A855F7', '#C026D2', '#DB2777', '#E11D48', '#6B7280'
];

const TagAnalytics = () => {
  const { userProfile } = useAuthStore();
  const { tags, subscribeToTags, createTag, updateTag, deleteTag, loading, error } = useTagStore();
  const { expenses, subscribeToExpenses, getExpensesByTags } = useExpenseStore();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: TAG_COLORS[0]
  });
  const [formError, setFormError] = useState('');
  const [selectedTagForAnalysis, setSelectedTagForAnalysis] = useState(null);
  const [analysisTimeRange, setAnalysisTimeRange] = useState('all'); // 'all', 'month', 'quarter', 'year'

  useEffect(() => {
    if (userProfile?.familyId) {
      subscribeToTags(userProfile.familyId);
      subscribeToExpenses(userProfile.familyId);
    }
  }, [userProfile?.familyId, subscribeToTags, subscribeToExpenses]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: TAG_COLORS[0]
    });
    setFormError('');
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Tag name is required');
      return;
    }

    const result = await createTag(userProfile.familyId, userProfile.uid, {
      name: formData.name.trim(),
      description: formData.description.trim(),
      color: formData.color
    });

    if (result.success) {
      setShowCreateModal(false);
      resetForm();
    } else {
      setFormError(result.error);
    }
  };

  const handleEditTag = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Tag name is required');
      return;
    }

    const result = await updateTag(userProfile.familyId, selectedTag.id, {
      name: formData.name.trim(),
      description: formData.description.trim(),
      color: formData.color
    });

    if (result.success) {
      setShowEditModal(false);
      setSelectedTag(null);
      resetForm();
    } else {
      setFormError(result.error);
    }
  };

  const handleDeleteTag = async () => {
    const result = await deleteTag(userProfile.familyId, selectedTag.id);
    
    if (result.success) {
      setShowDeleteModal(false);
      setSelectedTag(null);
    }
  };

  const openEditModal = (tag) => {
    setSelectedTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || '',
      color: tag.color
    });
    setFormError('');
    setShowEditModal(true);
  };

  const openDeleteModal = (tag) => {
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  // Get expenses for a specific tag
  const getExpensesForTag = (tagId) => {
    return expenses.filter(expense => expense.tags && expense.tags.includes(tagId));
  };

  // Calculate tag statistics
  const getTagStats = (tagId) => {
    const tagExpenses = getExpensesForTag(tagId);
    const totalAmount = tagExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const averageAmount = tagExpenses.length > 0 ? totalAmount / tagExpenses.length : 0;
    
    return {
      totalAmount,
      averageAmount,
      expenseCount: tagExpenses.length,
      expenses: tagExpenses
    };
  };

  // Get all tag statistics
  const getAllTagStats = () => {
    return tags.map(tag => ({
      ...tag,
      stats: getTagStats(tag.id)
    })).sort((a, b) => b.stats.totalAmount - a.stats.totalAmount);
  };

  // Get time-filtered expenses
  const getTimeFilteredExpenses = (expensesList) => {
    if (analysisTimeRange === 'all') return expensesList;
    
    const now = new Date();
    let startDate;
    
    switch (analysisTimeRange) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return expensesList;
    }
    
    return expensesList.filter(expense => {
      const expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
      return expenseDate >= startDate;
    });
  };

  const tagStats = getAllTagStats();
  const totalTaggedExpenses = expenses.filter(expense => expense.tags && expense.tags.length > 0);
  const totalTaggedAmount = totalTaggedExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  if (loading && tags.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/analytics">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tags</h1>
            <p className="text-gray-600">Loading tag data...</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/analytics">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tags</h1>
            <p className="text-gray-600">Manage tags and analyze expense patterns</p>
          </div>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" />
          Create Tag
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <Tag className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Tags</p>
                <p className="text-2xl font-bold text-gray-900">{tags.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tagged Expenses</p>
                <p className="text-2xl font-bold text-gray-900">{totalTaggedExpenses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Tagged Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalTaggedAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg per Tagged Expense</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(totalTaggedExpenses.length > 0 ? totalTaggedAmount / totalTaggedExpenses.length : 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Analysis Time Range</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            {[
              { value: 'all', label: 'All Time' },
              { value: 'year', label: 'This Year' },
              { value: 'quarter', label: 'This Quarter' },
              { value: 'month', label: 'This Month' }
            ].map((range) => (
              <Button
                key={range.value}
                variant={analysisTimeRange === range.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAnalysisTimeRange(range.value)}
              >
                {range.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tag Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <PieChart className="h-5 w-5" />
            <span>Tag Performance</span>
          </CardTitle>
          <CardDescription>
            Tags ranked by total spending amount
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tagStats.length === 0 ? (
            <div className="text-center py-8">
              <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No tags created yet</p>
              <p className="text-sm text-gray-400">Create your first tag to start analyzing expenses</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tagStats.map((tag, index) => {
                const timeFilteredExpenses = getTimeFilteredExpenses(tag.stats.expenses);
                const timeFilteredAmount = timeFilteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
                const percentage = totalTaggedAmount > 0 ? (timeFilteredAmount / totalTaggedAmount) * 100 : 0;
                
                return (
                  <div
                    key={tag.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                        <div>
                          <h3 className="text-sm font-medium text-gray-900">
                            {tag.name}
                          </h3>
                          {tag.description && (
                            <p className="text-xs text-gray-500">
                              {tag.description}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-900">
                              {formatCurrency(timeFilteredAmount)}
                            </span>
                            <span className="text-gray-500 ml-1">
                              ({timeFilteredExpenses.length} expenses)
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full"
                                style={{ 
                                  width: `${Math.min(percentage, 100)}%`,
                                  backgroundColor: tag.color
                                }}
                              />
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {percentage.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedTagForAnalysis(tag)}
                        className="h-8 w-8 p-0"
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(tag)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteModal(tag)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tag Detail Analysis */}
      {selectedTagForAnalysis && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: selectedTagForAnalysis.color }}
              />
              <span>{selectedTagForAnalysis.name} Analysis</span>
            </CardTitle>
            <CardDescription>
              Detailed breakdown of expenses for this tag
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(getTagStats(selectedTagForAnalysis.id).totalAmount)}
                  </p>
                  <p className="text-sm text-gray-600">Total Spent</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {getTagStats(selectedTagForAnalysis.id).expenseCount}
                  </p>
                  <p className="text-sm text-gray-600">Total Expenses</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(getTagStats(selectedTagForAnalysis.id).averageAmount)}
                  </p>
                  <p className="text-sm text-gray-600">Average per Expense</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Expenses</h4>
                <div className="space-y-2">
                  {getExpensesForTag(selectedTagForAnalysis.id)
                    .sort((a, b) => {
                      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
                      return dateB - dateA;
                    })
                    .slice(0, 10)
                    .map((expense) => {
                      const expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
                      return (
                        <div key={expense.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                            <p className="text-xs text-gray-500">{formatDate(expenseDate)}</p>
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(expense.amount)}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Tag Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Tag"
      >
        <form onSubmit={handleCreateTag} className="space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {formError}
            </div>
          )}
          
          <div>
            <label htmlFor="tagName" className="block text-sm font-medium text-gray-700 mb-1">
              Tag Name *
            </label>
            <Input
              id="tagName"
              type="text"
              placeholder="e.g., Marriage, Vacation, Emergency"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={formError && !formData.name.trim() ? 'border-red-500' : ''}
            />
          </div>

          <div>
            <label htmlFor="tagDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="tagDescription"
              rows={3}
              placeholder="Brief description of what this tag represents..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Palette className="h-4 w-4 inline mr-1" />
              Color
            </label>
            <div className="grid grid-cols-9 gap-2">
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formData.color === color 
                      ? 'border-gray-900 scale-110' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Tag'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowCreateModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Tag Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Tag"
      >
        <form onSubmit={handleEditTag} className="space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {formError}
            </div>
          )}
          
          <div>
            <label htmlFor="editTagName" className="block text-sm font-medium text-gray-700 mb-1">
              Tag Name *
            </label>
            <Input
              id="editTagName"
              type="text"
              placeholder="e.g., Marriage, Vacation, Emergency"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={formError && !formData.name.trim() ? 'border-red-500' : ''}
            />
          </div>

          <div>
            <label htmlFor="editTagDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="editTagDescription"
              rows={3}
              placeholder="Brief description of what this tag represents..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Palette className="h-4 w-4 inline mr-1" />
              Color
            </label>
            <div className="grid grid-cols-9 gap-2">
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formData.color === color 
                      ? 'border-gray-900 scale-110' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Updating...' : 'Update Tag'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowEditModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Tag"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the tag "{selectedTag?.name}"? This action cannot be undone.
          </p>
          {selectedTag?.usageCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-md text-sm">
              <strong>Warning:</strong> This tag is currently used by {selectedTag.usageCount} expense{selectedTag.usageCount !== 1 ? 's' : ''}. 
              You cannot delete a tag that is being used.
            </div>
          )}
          <div className="flex space-x-3 pt-4">
            <Button 
              onClick={handleDeleteTag} 
              disabled={loading || (selectedTag?.usageCount > 0)}
              variant="destructive"
              className="flex-1"
            >
              {loading ? 'Deleting...' : 'Delete Tag'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TagAnalytics;
